const uniqueStrings = (values = []) => Array.from(new Set(
    (Array.isArray(values) ? values : [])
        .map(value => String(value || '').trim())
        .filter(Boolean),
));

const activityIdFromRef = (ref) => {
    if (typeof ref === 'string' || typeof ref === 'number') return String(ref).trim();
    if (!ref || typeof ref !== 'object') return '';
    return String(ref.activityId || ref.activity_id || ref.id || '').trim();
};

const contributionKey = (entry) => JSON.stringify({
    activityRefs: entry.activityRefs,
    sourceScopeItemRefs: entry.sourceScopeItemRefs,
    costBreakdown: entry.costBreakdown,
});

/**
 * A folded payer row can contain one exact builder-edited ledger alongside
 * generated sibling activity costs. Persist the manual contribution and its
 * lineage separately; stamping the entire aggregate `manual_edit` would freeze
 * the generated sibling on every later WMS refresh.
 */
export function normaliseManualCostLedgerContributions(item = {}) {
    const raw = [
        item?._manualCostLedgerContributions,
        item?.manualCostLedgerContributions,
        item?._manual_cost_ledger_contributions,
        item?.manual_cost_ledger_contributions,
    ].flatMap(container => (Array.isArray(container) ? container : []));
    const seen = new Set();
    const output = [];
    for (const entry of raw) {
        const costBreakdown = entry?.costBreakdown || entry?.cost_breakdown;
        if (!costBreakdown || typeof costBreakdown !== 'object') continue;
        const normalised = {
            activityRefs: uniqueStrings([
                ...(Array.isArray(entry?.activityRefs) ? entry.activityRefs.map(activityIdFromRef) : []),
                ...(Array.isArray(entry?.activity_refs) ? entry.activity_refs.map(activityIdFromRef) : []),
            ]),
            sourceScopeItemRefs: uniqueStrings([
                ...(Array.isArray(entry?.sourceScopeItemRefs) ? entry.sourceScopeItemRefs : []),
                ...(Array.isArray(entry?.source_scope_item_refs) ? entry.source_scope_item_refs : []),
            ]),
            costBreakdown,
        };
        const key = contributionKey(normalised);
        if (seen.has(key)) continue;
        seen.add(key);
        output.push(normalised);
    }
    return output;
}

export function canonicaliseManualCostLedgerOwner(item = {}) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
    const costBreakdown = item.costBreakdown || item.cost_breakdown;
    const contributions = normaliseManualCostLedgerContributions(item);
    const hasAlias = Object.prototype.hasOwnProperty.call(item, 'cost_breakdown')
        || Object.prototype.hasOwnProperty.call(item, 'manualCostLedgerContributions')
        || Object.prototype.hasOwnProperty.call(item, '_manual_cost_ledger_contributions')
        || Object.prototype.hasOwnProperty.call(item, 'manual_cost_ledger_contributions');
    const hasNonCanonicalContribution = Array.isArray(item?._manualCostLedgerContributions)
        && item._manualCostLedgerContributions.some(entry => (
            Object.prototype.hasOwnProperty.call(entry || {}, 'cost_breakdown')
            || Object.prototype.hasOwnProperty.call(entry || {}, 'activity_refs')
            || Object.prototype.hasOwnProperty.call(entry || {}, 'source_scope_item_refs')
        ));
    if (!hasAlias && !hasNonCanonicalContribution) return item;
    const {
        cost_breakdown: _costBreakdownAlias,
        manualCostLedgerContributions: _camelContainerAlias,
        _manual_cost_ledger_contributions: _privateSnakeContainerAlias,
        manual_cost_ledger_contributions: _snakeContainerAlias,
        ...canonical
    } = item;
    return {
        ...canonical,
        ...(costBreakdown && typeof costBreakdown === 'object' ? { costBreakdown } : {}),
        ...(contributions.length > 0 ? { _manualCostLedgerContributions: contributions } : {}),
    };
}

export function hasPartialManualCostLedgerAuthority(item = {}) {
    return normaliseManualCostLedgerContributions(item).length > 0;
}

export function buildManualCostLedgerContribution(item = {}, activities = []) {
    const canonicalItem = canonicaliseManualCostLedgerOwner(item);
    if (!canonicalItem?.costBreakdown || typeof canonicalItem.costBreakdown !== 'object') return null;
    const directActivityRefs = uniqueStrings([
        ...(Array.isArray(canonicalItem?.activityRefs) ? canonicalItem.activityRefs.map(activityIdFromRef) : []),
        ...(Array.isArray(canonicalItem?.activity_refs) ? canonicalItem.activity_refs.map(activityIdFromRef) : []),
        ...(Array.isArray(canonicalItem?.activity_ids) ? canonicalItem.activity_ids : []),
        ...(Array.isArray(canonicalItem?.activityIds) ? canonicalItem.activityIds : []),
        activityIdFromRef(canonicalItem?.activityRef || canonicalItem?.activity_ref),
    ]);
    const sourceScopeItemRefs = uniqueStrings([
        canonicalItem?.id,
        canonicalItem?.scopeItemRef,
        canonicalItem?.scope_item_ref,
    ]);
    const inferredActivityRefs = (Array.isArray(activities) ? activities : [])
        .filter(activity => sourceScopeItemRefs.includes(String(activity?.scopeItemRef || activity?.scope_item_ref || '').trim()))
        .map(activity => String(activity?.id || '').trim());
    return {
        activityRefs: uniqueStrings([...directActivityRefs, ...inferredActivityRefs]),
        sourceScopeItemRefs,
        costBreakdown: canonicalItem.costBreakdown,
    };
}

export function resolveManualCostLedgerContributions(item = {}, activities = []) {
    const canonicalItem = canonicaliseManualCostLedgerOwner(item);
    const contributions = normaliseManualCostLedgerContributions(canonicalItem);
    const currentActivities = Array.isArray(activities) ? activities : [];
    if (contributions.length === 0) {
        return { ok: true, item: canonicalItem, contributions, freshActivities: currentActivities, receipt: null };
    }

    const currentActivityIds = new Set(currentActivities
        .map(activity => String(activity?.id || '').trim())
        .filter(Boolean));
    const missingActivityRefs = uniqueStrings(contributions.flatMap(entry => (
        entry.activityRefs.length === 0
            ? ['(missing)']
            : entry.activityRefs.filter(ref => !currentActivityIds.has(ref))
    )));
    if (missingActivityRefs.length > 0) {
        return {
            ok: false,
            item: canonicalItem,
            contributions,
            freshActivities: [],
            receipt: {
                status: 'unresolved',
                reason: 'missing-or-changed-lineage',
                manualReviewRequired: true,
                missingActivityRefs,
                contributionCount: contributions.length,
            },
        };
    }

    const claimCounts = new Map();
    for (const entry of contributions) {
        for (const ref of entry.activityRefs) claimCounts.set(ref, (claimCounts.get(ref) || 0) + 1);
    }
    const overlappingActivityRefs = [...claimCounts.entries()]
        .filter(([, count]) => count > 1)
        .map(([ref]) => ref)
        .sort();
    if (overlappingActivityRefs.length > 0) {
        // Keep the first complete non-overlapping claim in persisted order.
        // This gives the blocked Stage 4 row a stable review value without
        // summing two ledgers that both say they own the same activity. Every
        // claimed activity remains excluded from generated refresh, including
        // claims that lost the deterministic tie-break.
        const selectedContributions = [];
        const selectedActivityRefs = new Set();
        for (const entry of contributions) {
            if (entry.activityRefs.some(ref => selectedActivityRefs.has(ref))) continue;
            selectedContributions.push(entry);
            entry.activityRefs.forEach(ref => selectedActivityRefs.add(ref));
        }
        const allClaimedActivityRefs = new Set(contributions.flatMap(entry => entry.activityRefs));
        const freshActivities = currentActivities.filter(activity => (
            !allClaimedActivityRefs.has(String(activity?.id || '').trim())
        ));
        return {
            ok: false,
            item: canonicalItem,
            contributions,
            selectedContributions,
            freshActivities,
            receipt: {
                status: 'unresolved',
                reason: 'overlapping-manual-claims',
                manualReviewRequired: true,
                overlappingActivityRefs,
                contributionCount: contributions.length,
                selectedContributionCount: selectedContributions.length,
                selectionPolicy: 'first-non-overlapping-claim',
            },
        };
    }

    const manualActivityRefs = new Set(contributions.flatMap(entry => entry.activityRefs));
    const freshActivities = currentActivities.filter(activity => (
        !manualActivityRefs.has(String(activity?.id || '').trim())
    ));
    return {
        ok: true,
        item: canonicalItem,
        contributions,
        selectedContributions: contributions,
        freshActivities,
        receipt: {
            status: 'resolved',
            manualReviewRequired: false,
            contributionCount: contributions.length,
            ownedActivityRefs: [...manualActivityRefs].sort(),
        },
    };
}

export function manualCostLedgerResolutionFields(resolution) {
    if (!resolution || resolution.contributions?.length === 0) return {};
    const existingBlocker = resolution.item?._scopeProjectionBlocker;
    if (!resolution.ok) {
        return {
            _scopeProjectionBlocker: existingBlocker || 'manual-cost-lineage-unresolved',
            _manualCostLedgerResolution: resolution.receipt,
        };
    }
    return {
        _scopeProjectionBlocker: existingBlocker === 'manual-cost-lineage-unresolved'
            ? undefined
            : existingBlocker,
        _manualCostLedgerResolution: resolution.receipt,
    };
}

export function partitionActivitiesByManualCostContributions(item = {}, activities = []) {
    return resolveManualCostLedgerContributions(item, activities);
}
